import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "../api/homeapi";
import Swal from "sweetalert2";

/* ─── HELPERS ─── */
const getPhotoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const BASE = (api.defaults.baseURL || "").replace(/\/api$/, "");
  return `${BASE}${url.startsWith("/") ? "" : "/"}${url}`.replace(/([^:])\/\//g, "$1/");
};

/* ─── ICONS ─── */
const Icon = ({ d, size = 16, fill = "none" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d={d} />
  </svg>
);
//const IcoUsers   = () => <Icon d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />;
const IcoRefresh = () => <Icon d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />;

const CloseIcon  = () => <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>;
const CheckIcon  = () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8l4 4 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const CancelIcon = () => <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.3"/><path d="M5 8h6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>;
const DownloadIcon = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 2v8M5 7l3 3 3-3M3 12h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const LocationIcon = () => <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M8 1.5A4.5 4.5 0 013.5 6c0 3.75 4.5 8.5 4.5 8.5s4.5-4.75 4.5-8.5A4.5 4.5 0 018 1.5z" stroke="currentColor" strokeWidth="1.3"/><circle cx="8" cy="6" r="1.5" stroke="currentColor" strokeWidth="1.3"/></svg>;
const PhoneIcon  = () => <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M3 2h3l1.5 3.5-2 1a9 9 0 004 4l1-2 3.5 1.5V13a1 1 0 01-1 1C5 14 2 8 2 3a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/></svg>;
const CardIcon   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M2 10h20M6 15h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>;

/* ─── PAYMENT CONSTANTS ─── */
const OPERATORS = ["MVola", "Orange Money", "Airtel Money"];
const OPERATOR_NUMBERS = {
  "MVola":        "034 570 0524",
  "Orange Money": "037 313 3441",
  "Airtel Money": "033 579 2739",
};
const OPERATOR_USSD = {
  "MVola":        { code: "*MVola#", menu: "Envoyer de l'argent → Vers un numéro" },
  "Orange Money": { code: "#144#",   menu: "Transfert → Vers un numéro" },
  "Airtel Money": { code: "*185#",   menu: "Envoyer de l'argent → Vers un numéro" },
};

/* ─── CITY AUTOCOMPLETE ─── */
const CityAutocomplete = ({ label, value, onChange, placeholder = "Rechercher une ville…" }) => {
  const [all, setAll]     = useState([]);
  const [query, setQuery] = useState(value?.name || "");
  const [open, setOpen]   = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onClickOutside = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/cities")
      .then(res => setAll(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const suggestions = query.trim().length >= 1
    ? all.filter(c => c.name.toLowerCase().includes(query.toLowerCase())).slice(0, 8)
    : all.slice(0, 8);

  const IcoX = () => <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>;
  const select = city => { setQuery(city.name); onChange(city); setOpen(false); };
  const clear  = ()   => { setQuery(""); onChange(null); };

  return (
    <div style={s.formField} ref={wrapRef}>
      {label && <label style={s.fieldLabel}>{label}</label>}
      <div style={{ position: "relative" }}>
        <input
          style={{ ...s.fieldInput, paddingRight: 34 }}
          placeholder={loading ? "Chargement…" : placeholder}
          value={query}
          autoComplete="off"
          onChange={e => { setQuery(e.target.value); onChange(null); setOpen(true); }}
          onFocus={() => setOpen(true)}
        />
        {query && (
          <button onClick={clear} style={s.clearBtn} type="button"><IcoX /></button>
        )}
        {open && suggestions.length > 0 && (
          <ul style={s.dropdown}>
            {suggestions.map(c => (
              <li key={c.id} style={s.dropdownItem}
                onMouseDown={e => { e.preventDefault(); select(c); }}
                onMouseEnter={e => e.currentTarget.style.background = "#f0fdf4"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                {c.name}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

/* ─── STARS ─── */
const Stars = ({ rating }) => (
  <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
    {[1,2,3,4,5].map(i => (
      <span key={i} style={{ color: i <= Math.round(rating) ? "#f59e0b" : "#e0e0e0", fontSize: 11 }}>★</span>
    ))}
    <span style={{ fontSize: 11, color: "#666", marginLeft: 2 }}>{rating}</span>
  </span>
);

/* ─── VEHICLE PHOTOS GALLERY ─── */
const VehiclePhotos = ({ photos }) => {
  const [activeIdx, setActiveIdx] = useState(0);
  if (!photos || photos.length === 0) return null;

  const sorted = [...photos].sort((a, b) => {
    const aInt = /int[eé]r/i.test(a) ? 0 : 1;
    const bInt = /int[eé]r/i.test(b) ? 0 : 1;
    return aInt - bInt;
  });

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ position: "relative", borderRadius: 10, overflow: "hidden", height: 160, background: "#f0f0f0", marginBottom: 6 }}>
        <img
          src={getPhotoUrl(sorted[activeIdx])}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          onError={e => { e.target.style.display = "none"; }}
        />
        {sorted.length > 1 && (
          <>
            <button onClick={() => setActiveIdx(i => (i - 1 + sorted.length) % sorted.length)}
              style={{ position: "absolute", left: 6, top: "50%", transform: "translateY(-50%)", background: "rgba(0,0,0,0.45)", border: "none", borderRadius: 6, color: "white", width: 28, height: 28, cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>‹</button>
            <button onClick={() => setActiveIdx(i => (i + 1) % sorted.length)}
              style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", background: "rgba(0,0,0,0.45)", border: "none", borderRadius: 6, color: "white", width: 28, height: 28, cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>›</button>
          </>
        )}
        <div style={{ position: "absolute", bottom: 6, right: 8, fontSize: 10, color: "white", background: "rgba(0,0,0,0.4)", borderRadius: 4, padding: "2px 6px" }}>
          {activeIdx + 1}/{sorted.length}
        </div>
        <div style={{ position: "absolute", top: 8, left: 8, fontSize: 10, color: "white", background: /int[eé]r/i.test(sorted[activeIdx]) ? "rgba(22,163,74,0.85)" : "rgba(15,23,42,0.75)", borderRadius: 4, padding: "2px 7px", fontWeight: 600 }}>
          {/int[eé]r/i.test(sorted[activeIdx]) ? "Intérieur" : "Extérieur"}
        </div>
      </div>
      {sorted.length > 1 && (
        <div style={{ display: "flex", gap: 5, overflowX: "auto", paddingBottom: 2 }}>
          {sorted.map((p, i) => (
            <div key={i} onClick={() => setActiveIdx(i)}
              style={{ flexShrink: 0, width: 52, height: 38, borderRadius: 6, overflow: "hidden", cursor: "pointer", border: i === activeIdx ? "2px solid #16a34a" : "2px solid transparent", opacity: i === activeIdx ? 1 : 0.65, transition: "all .15s" }}>
              <img src={getPhotoUrl(p)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={e => { e.target.style.display = "none"; }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── MISC HELPERS ─── */
const ACCENTS = ["#16a34a","#3b82f6","#9333ea","#d97706","#ea580c","#06b6d4","#e11d48"];
const accentFor = id => ACCENTS[(id || 0) % ACCENTS.length];
const initials  = name => (name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
const fmtPrice  = n => Number(n || 0).toLocaleString("fr-MG");
const fmtTime   = iso => iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "—";
const fmtDate   = iso => iso ? new Date(iso).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" }) : "";

/**
 * Returns true if the trip's departure time is in the past.
 * Works whether trip.date is a full ISO datetime or just a date string.
 
const isTripPast = (trip) => {
  if (!trip.date) return false;
  const d = new Date(trip.date);
  return d < new Date();
};
*/
/* ════════════════════════════════════════════════
   SEAT SVG
════════════════════════════════════════════════ */
const SeatSVG = ({ state }) => {
  const colors = {
    free:     { body: "#E8EDF2", cushion: "#D0D8E0", back: "#DDE3EA", leg: "#B8C4CE", arm: "#C8D2DC", shadow: "rgba(0,0,0,0.08)" },
    selected: { body: "#16a34a", cushion: "#15803d", back: "#14532d", leg: "#166534", arm: "#15803d", shadow: "rgba(22,163,74,0.3)" },
    taken:    { body: "#FEE2E2", cushion: "#FECACA", back: "#FECACA", leg: "#FCA5A5", arm: "#FCA5A5", shadow: "rgba(239,68,68,0.12)" },
  };
  const c = colors[state] || colors.free;
  return (
    <svg viewBox="0 0 56 72" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "auto", display: "block" }}>
      <ellipse cx="28" cy="68" rx="18" ry="3.5" fill={c.shadow} />
      <rect x="8" y="4" width="40" height="30" rx="6" fill={c.body} />
      <rect x="12" y="7" width="32" height="23" rx="4" fill={c.back} opacity="0.7" />
      <line x1="28" y1="8" x2="28" y2="29" stroke="white" strokeWidth="0.8" strokeDasharray="2 2" opacity="0.5" />
      <line x1="12" y1="18" x2="44" y2="18" stroke="white" strokeWidth="0.8" strokeDasharray="2 2" opacity="0.5" />
      <rect x="8" y="4" width="40" height="6" rx="6" fill={c.body} opacity="0.6" />
      <rect x="3" y="28" width="7" height="14" rx="3" fill={c.arm} />
      <rect x="2" y="34" width="9" height="5" rx="2.5" fill={c.leg} />
      <rect x="46" y="28" width="7" height="14" rx="3" fill={c.arm} />
      <rect x="45" y="34" width="9" height="5" rx="2.5" fill={c.leg} />
      <rect x="6" y="34" width="44" height="20" rx="5" fill={c.body} />
      <rect x="10" y="37" width="36" height="14" rx="3" fill={c.cushion} opacity="0.8" />
      <line x1="10" y1="44" x2="46" y2="44" stroke="white" strokeWidth="0.8" strokeDasharray="2 2" opacity="0.5" />
      <rect x="12" y="54" width="6" height="10" rx="3" fill={c.leg} />
      <rect x="38" y="54" width="6" height="10" rx="3" fill={c.leg} />
      <rect x="15" y="60" width="26" height="3" rx="1.5" fill={c.leg} opacity="0.6" />
      {state === "taken" && (
        <g>
          <rect x="8" y="4" width="40" height="50" rx="5" fill="rgba(239,68,68,0.15)" />
          <line x1="16" y1="14" x2="40" y2="38" stroke="#DC2626" strokeWidth="4" strokeLinecap="round" />
          <line x1="40" y1="14" x2="16" y2="38" stroke="#DC2626" strokeWidth="4" strokeLinecap="round" />
          <circle cx="28" cy="26" r="13" fill="none" stroke="#DC2626" strokeWidth="2" opacity="0.5" />
        </g>
      )}
      {state === "selected" && (
        <g>
          <path d="M18 26 L25 33 L38 18" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </g>
      )}
    </svg>
  );
};

/* ════════════════════════════════════════════════
   SEAT PICKER MODAL
════════════════════════════════════════════════ */
const SeatPicker = ({ trip, onSelect, onClose }) => {
  const totalSeats = Number(trip.totalSeats) || 14;
  const taken      = (trip.takenSeats || []).map(Number);
  const [selected, setSelected] = useState(null);

  const isBus    = totalSeats > 8;
  const colCount = isBus ? 4 : 3;
  const rows     = Math.ceil(totalSeats / colCount);
  const seats    = Array.from({ length: totalSeats }, (_, i) => i + 1);

  const getState = (num) => {
    if (taken.includes(num)) return "taken";
    if (selected === num)    return "selected";
    return "free";
  };
  const handleClick = (num) => {
    if (taken.includes(num)) return;
    setSelected(num === selected ? null : num);
  };

  const available = seats.filter(n => !taken.includes(n)).length;

  const SeatGrid = () => {
    const rowElements = [];
    for (let r = 0; r < rows; r++) {
      const rowSeats = [];
      const leftCols  = isBus ? 2 : 1;
      const rightCols = 2;

      for (let c = 0; c < leftCols; c++) {
        const num = r * colCount + c + 1;
        if (num > totalSeats) { rowSeats.push(<div key={`L${c}`} />); continue; }
        const state = getState(num);
        rowSeats.push(
          <div key={`seat-${num}`} onClick={() => handleClick(num)}
            title={state === "taken" ? `Siège ${num} — Occupé` : `Siège ${num}`}
            style={{ cursor: state === "taken" ? "not-allowed" : "pointer", transition: "transform 0.15s", userSelect: "none" }}
            onMouseEnter={e => { if (state !== "taken") e.currentTarget.style.transform = "scale(1.08) translateY(-2px)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}>
            <SeatSVG state={state} />
            <div style={{ textAlign: "center", fontSize: 10, fontWeight: 700, marginTop: 2, color: state === "taken" ? "#DC2626" : state === "selected" ? "#16a34a" : "#94a3b8" }}>{num}</div>
          </div>
        );
      }

      rowSeats.push(
        <div key="aisle" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ width: 2, height: "60%", background: "#e2e8f0", borderRadius: 1 }} />
        </div>
      );

      for (let c = 0; c < rightCols; c++) {
        const num = r * colCount + leftCols + c + 1;
        if (num > totalSeats) { rowSeats.push(<div key={`R${c}`} />); continue; }
        const state = getState(num);
        rowSeats.push(
          <div key={`seat-${num}`} onClick={() => handleClick(num)}
            title={state === "taken" ? `Siège ${num} — Occupé` : `Siège ${num}`}
            style={{ cursor: state === "taken" ? "not-allowed" : "pointer", transition: "transform 0.15s", userSelect: "none" }}
            onMouseEnter={e => { if (state !== "taken") e.currentTarget.style.transform = "scale(1.08) translateY(-2px)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}>
            <SeatSVG state={state} />
            <div style={{ textAlign: "center", fontSize: 10, fontWeight: 700, marginTop: 2, color: state === "taken" ? "#DC2626" : state === "selected" ? "#16a34a" : "#94a3b8" }}>{num}</div>
          </div>
        );
      }

      rowElements.push(
        <div key={`row-${r}`} style={{ display: "grid", gridTemplateColumns: isBus ? "1fr 1fr 20px 1fr 1fr" : "1fr 20px 1fr 1fr", gap: 8, alignItems: "end", padding: "0 4px" }}>
          {rowSeats}
        </div>
      );
    }
    return <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>{rowElements}</div>;
  };

  return (
    <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "white", borderRadius: 20, width: "100%", maxWidth: 520, maxHeight: "92vh", overflowY: "auto", boxShadow: "0 32px 80px rgba(0,0,0,0.25)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 22px 14px", borderBottom: "1px solid #f0f0f0", position: "sticky", top: 0, background: "white", zIndex: 1, borderRadius: "20px 20px 0 0" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#1a1a1a" }}>Choisissez votre siège</div>
            <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{available} siège{available > 1 ? "s" : ""} disponible{available > 1 ? "s" : ""}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 8, cursor: "pointer", padding: "6px 8px", color: "#94a3b8", display: "flex" }}>
            <svg width="16" height="16" viewBox="0 0 14 14" fill="none"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
          </button>
        </div>
        <div style={{ padding: "16px 20px 24px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, background: "#f8fafc", borderRadius: 10, padding: "10px 16px", marginBottom: 16, border: "1px solid #e2e8f0" }}>
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
              <circle cx="16" cy="16" r="12" stroke="#64748b" strokeWidth="2" fill="none"/>
              <circle cx="16" cy="16" r="4"  stroke="#64748b" strokeWidth="2" fill="none"/>
              <line x1="16" y1="4"  x2="16" y2="12" stroke="#64748b" strokeWidth="2" strokeLinecap="round"/>
              <line x1="16" y1="20" x2="16" y2="28" stroke="#64748b" strokeWidth="2" strokeLinecap="round"/>
              <line x1="4"  y1="16" x2="12" y2="16" stroke="#64748b" strokeWidth="2" strokeLinecap="round"/>
              <line x1="20" y1="16" x2="28" y2="16" stroke="#64748b" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600, letterSpacing: "0.3px" }}>CHAUFFEUR — AVANT DU VÉHICULE</span>
          </div>
          <div style={{ background: "#f8fafc", borderRadius: 14, padding: "16px 12px", border: "1px solid #e2e8f0", marginBottom: 16 }}>
            <SeatGrid />
          </div>
          <div style={{ display: "flex", gap: 20, justifyContent: "center", marginBottom: 20, flexWrap: "wrap" }}>
            {[["Libre","#E8EDF2","#B8C4CE",null],["Sélectionné","#16a34a","#14532d",null],["Occupé","#FEE2E2","#FCA5A5","✕"]].map(([label,bg,border,dot]) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#64748b" }}>
                <div style={{ width: 18, height: 18, background: bg, border: `1.5px solid ${border}`, borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#DC2626", fontWeight: 900 }}>{dot}</div>
                {label}
              </div>
            ))}
          </div>
          <button disabled={!selected} onClick={() => selected && onSelect(selected)}
            style={{ width: "100%", padding: "13px", background: selected ? "#16a34a" : "#e2e8f0", color: selected ? "white" : "#94a3b8", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: selected ? "pointer" : "not-allowed", fontFamily: "inherit", transition: "all 0.2s", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            {selected ? (<><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8l4 4 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>Confirmer le siège N°{selected}</>) : "Sélectionnez un siège"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════
   BOOKING MODAL
════════════════════════════════════════════════ */
const BookingModal = ({ trip, seatNumber, onClose, onConfirm }) => {
  const [fullName, setFullName] = useState("");
  const [err, setErr]           = useState("");

  const from = trip.departureCity?.name || trip.departureCityName || trip.departure || "—";
  const to   = trip.arrivalCity?.name   || trip.arrivalCityName   || trip.arrival   || "—";

  const getCancelDeadline = () => {
    const dateStr = trip.date;
    if (!dateStr) return null;
    const dt = new Date(dateStr);
    dt.setHours(dt.getHours() - 2);
    return dt.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  };
  const deadline = getCancelDeadline();

  return (
    <Overlay onClose={onClose}>
      <div style={modalStyle}>
        <ModalHeader title="Informations passager" onClose={onClose} />
        <div style={{ padding: "0 24px 24px" }}>
          <div style={tripSummaryCard}>
            <div style={tsRow}><span style={tsLabel}>Trajet</span><span style={tsVal}>{from} → {to}</span></div>
            {trip.stationName && (
              <div style={tsRow}><span style={tsLabel}>Gare</span><span style={{ ...tsVal, display: "flex", alignItems: "center", gap: 4 }}><LocationIcon /> {trip.stationName}</span></div>
            )}
            {seatNumber && (
              <div style={tsRow}><span style={tsLabel}>Siège</span><span style={{ ...tsVal, fontWeight: 700, color: "#16a34a" }}>N°{seatNumber}</span></div>
            )}
            <div style={tsRow}><span style={tsLabel}>Prix</span><span style={{ ...tsVal, fontWeight: 700 }}>{fmtPrice(trip.price)} MGA</span></div>
            {trip.tripType && <div style={tsRow}><span style={tsLabel}>Type</span><span style={tsVal}>{trip.tripType}</span></div>}
          </div>
          <div style={cancelNotice}>
            <span style={{ color: "#059669", display: "flex", flexShrink: 0, marginTop: 1 }}><CancelIcon /></span>
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: "#065f46", marginBottom: 2 }}>Annulation gratuite</div>
              <div style={{ fontSize: 12, color: "#047857", lineHeight: 1.5 }}>Annulez sans frais jusqu'à <strong>12h avant le départ</strong>{deadline ? ` (avant le ${deadline})` : ""}.</div>
            </div>
          </div>
          <label style={fieldLabelStyle}>Nom complet *</label>
          <input style={inputField2} placeholder="Ex : Rakoto Jean" value={fullName}
            onChange={e => { setFullName(e.target.value); setErr(""); }} />
          {err && <p style={{ color: "#ef4444", fontSize: 12, marginTop: 4 }}>{err}</p>}
          <button style={{ ...btnPrimary, width: "100%", marginTop: 16 }}
            onClick={() => { if (!fullName.trim()) { setErr("Veuillez saisir votre nom"); return; } onConfirm({ fullName }); }}>
            Continuer →
          </button>
        </div>
      </div>
    </Overlay>
  );
};

/* ════════════════════════════════════════════════
   PAYMENT MODAL
════════════════════════════════════════════════ */
const PaymentModal = ({ amount, reference, onClose, onSuccess }) => {
  const [payMethod, setPayMethod] = useState("mobile");
  const [operator,  setOperator]  = useState("");
  const [txRef,     setTxRef]     = useState("");
  const [step,      setStep]      = useState(1);
  const [loading,   /*setLoading*/]   = useState(false);
  const [err,       setErr]       = useState("");

  const handleMobileNext = () => {
    if (step === 1) {
      if (!operator) { setErr("Choisissez un opérateur"); return; }
      setStep(2); setErr("");
    } else {
      if (!txRef.trim()) { setErr("Saisissez la référence de transaction"); return; }
      onSuccess({ operator, txRef });
    }
  };

  const StepBar = ({ current }) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18 }}>
      <div style={{ ...stepDot, ...(current >= 1 ? stepDotActive : {}) }}>1</div>
      <div style={stepLine} />
      <div style={{ ...stepDot, ...(current >= 2 ? stepDotActive : {}) }}>2</div>
    </div>
  );

  return (
    <Overlay onClose={onClose}>
      <div style={modalStyle}>
        <ModalHeader title="Paiement" onClose={onClose} />
        <div style={{ padding: "0 24px 24px" }}>
          <div style={amountBox}>
            <div style={amountLabel}>Montant à payer</div>
            <div style={amountVal}>{fmtPrice(amount)} <span style={{ fontSize: 14, color: "#999" }}>MGA</span></div>
            {reference && <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>Réf : {reference}</div>}
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            {[["mobile", <PhoneIcon />, "Mobile Money"], ["card", <CardIcon />, "Carte bancaire"]].map(([key, icon, label]) => (
              <button key={key} style={{ ...payMethodBtn, ...(payMethod === key ? payMethodBtnActive : {}) }}
                onClick={() => { setPayMethod(key); setErr(""); setStep(1); }}>
                {icon} {label}
              </button>
            ))}
          </div>
          {payMethod === "mobile" && (
            <>
              <StepBar current={step} />
              {step === 1 && (
                <>
                  <label style={fieldLabelStyle}>Choisissez votre opérateur *</label>
                  <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
                    {OPERATORS.map(op => (
                      <button key={op} style={{ ...opBtn, ...(operator === op ? opBtnActive : {}) }}
                        onClick={() => { setOperator(op); setErr(""); }}>
                        {op}
                      </button>
                    ))}
                  </div>
                  {operator && (
                    <div style={instructionBox}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: "#92400e", marginBottom: 6 }}>📲 Comment payer avec {operator}</div>
                      <p style={{ fontSize: 12, color: "#78350f", lineHeight: 1.6, margin: 0 }}>
                        1. Composez <strong>{OPERATOR_USSD[operator].code}</strong><br/>
                        2. Sélectionnez <strong>{OPERATOR_USSD[operator].menu}</strong><br/>
                        3. Entrez le numéro : <strong style={{ fontSize: 14, color: "#92400e", letterSpacing: 1 }}>{OPERATOR_NUMBERS[operator]}</strong><br/>
                        4. Montant : <strong>{fmtPrice(amount)} MGA</strong><br/>
                        5. Confirmez avec votre code PIN<br/>
                        6. Notez la <strong>référence de transaction</strong> reçue par SMS
                      </p>
                    </div>
                  )}
                </>
              )}
              {step === 2 && (
                <>
                  <div style={{ background: "#f0fdf4", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#1a1a1a", border: "0.5px solid #bbf7d0" }}>
                    <div style={{ marginBottom: 4 }}>Opérateur : <strong>{operator}</strong></div>
                    <div>Numéro destinataire : <strong style={{ color: "#16a34a" }}>{OPERATOR_NUMBERS[operator]}</strong></div>
                    <div style={{ marginTop: 4, fontSize: 12, color: "#999" }}>Montant : {fmtPrice(amount)} MGA</div>
                  </div>
                  <label style={fieldLabelStyle}>Référence de transaction reçue par SMS *</label>
                  <input style={inputField2} placeholder="Ex : MVL-2025-XXXXXX"
                    value={txRef} onChange={e => { setTxRef(e.target.value); setErr(""); }} />
                  <p style={{ fontSize: 11.5, color: "#999", marginTop: 6, lineHeight: 1.5 }}>Copiez exactement la référence reçue par SMS après votre paiement.</p>
                </>
              )}
            </>
          )}
          {payMethod === "card" && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "32px 16px", textAlign: "center", gap: 14 }}>
              <div style={{ fontSize: 40 }}>💳</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a" }}>Paiement par carte</div>
              <div style={{ background: "#fffbeb", border: "0.5px solid #fde68a", borderRadius: 12, padding: "16px 20px", width: "100%" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e", marginBottom: 6 }}>🚧 Service bientôt disponible</div>
                <div style={{ fontSize: 12.5, color: "#78350f", lineHeight: 1.65 }}>Le paiement par carte bancaire sera disponible très prochainement.<br/>En attendant, utilisez <strong>Mobile Money</strong>.</div>
              </div>
            </div>
          )}
          {err && <p style={{ color: "#ef4444", fontSize: 12, marginTop: 8 }}>{err}</p>}
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <button style={btnGhost} onClick={() => {
              if (step === 1 || payMethod === "card") onClose();
              else { setStep(1); setErr(""); }
            }}>← Retour</button>
            {payMethod === "mobile" && (
              <button style={{ ...btnPrimary, flex: 1 }} onClick={handleMobileNext} disabled={loading}>
                {loading
                  ? <span style={{ display: "inline-block", width: 14, height: 14, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "white", borderRadius: "50%", animation: "cv-spin .7s linear infinite" }} />
                  : step === 1 ? "Suivant →" : "Confirmer le paiement ✓"}
              </button>
            )}
          </div>
        </div>
      </div>
    </Overlay>
  );
};

/* ════════════════════════════════════════════════
   TICKET MODAL
════════════════════════════════════════════════ */
const TicketModal = ({ booking, operator, onClose }) => {
  const contactNumber = OPERATOR_NUMBERS[operator] || OPERATOR_NUMBERS["MVola"];
  const contactLabel  = operator && operator !== "Card"
    ? `${operator} : ${contactNumber}`
    : `MVola : ${OPERATOR_NUMBERS["MVola"]}`;

  const downloadTicket = () => {
    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Billet - ${booking.reference}</title>
<style>
  body{font-family:'Helvetica Neue',sans-serif;background:#fafaf8;margin:0;padding:40px}
  .ticket{max-width:480px;margin:auto;background:white;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,.1)}
  .header{background:linear-gradient(135deg,#14532d,#16a34a);color:white;padding:28px 32px 20px}
  .logo{font-size:22px;font-weight:800;margin-bottom:4px}.logo span{color:#86efac}
  .ref{font-size:11px;opacity:.7;letter-spacing:1px;text-transform:uppercase}
  .route{font-size:26px;font-weight:700;margin:12px 0 4px}
  .body{padding:24px 32px}
  .row{display:flex;justify-content:space-between;padding:10px 0;border-bottom:.5px solid #f0f0f0}
  .row:last-child{border-bottom:none}
  .label{font-size:11px;color:#999;text-transform:uppercase;letter-spacing:.5px}
  .val{font-size:13px;font-weight:600;color:#1a1a1a}
  .seat{font-size:28px;font-weight:800;color:#16a34a}
  .cancel-policy{background:#f0fdf4;padding:12px 16px;border-radius:8px;margin:12px 0;font-size:12px;color:#166534}
  .contact-box{background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:12px 16px;margin:8px 0;font-size:12px;color:#78350f}
  .footer{background:#f0fdf4;padding:16px 32px;text-align:center;font-size:11px;color:#16a34a;font-weight:600}
  .barcode{text-align:center;margin:20px 0 4px;letter-spacing:4px;font-size:11px;color:#ccc;font-family:monospace}
</style></head><body>
<div class="ticket">
  <div class="header">
    <div class="logo">Trans<span>TiGo</span></div>
    <div class="ref">Réf : ${booking.reference}</div>
    <div class="route">${booking.trip?.departure ?? "—"} → ${booking.trip?.arrival ?? "—"}</div>
  </div>
  <div class="body">
    <div class="row">
      <div><div class="label">Passager</div><div class="val">${booking.passenger ?? "—"}</div></div>
      ${booking.seat ? `<div><div class="label">Siège</div><div class="seat">N°${booking.seat}</div></div>` : ""}
    </div>
    ${booking.trip?.station ? `<div class="row"><div class="label">Gare de départ</div><div class="val">📍 ${booking.trip.station}</div></div>` : ""}
    <div class="row"><div class="label">Transporteur</div><div class="val">${booking.trip?.transporter ?? "—"}</div></div>
    <div class="row"><div class="label">Date & Heure</div><div class="val">${booking.trip?.date ?? "—"} · ${booking.trip?.departureTime ?? "—"}</div></div>
    <div class="row"><div class="label">Prix payé</div><div class="val">${Number(booking.trip?.price ?? 0).toLocaleString()} MGA</div></div>
    <div class="row"><div class="label">Statut</div><div class="val" style="color:#16a34a">✓ Confirmé</div></div>
    <div class="cancel-policy">⚪ Annulation gratuite jusqu'à 12h avant le départ.</div>
    <div class="contact-box">📞 ${contactLabel}</div>
  </div>
  <div class="barcode">||| ${booking.reference} |||</div>
  <div class="footer">Présentez ce billet au départ · TransTiGo Madagascar</div>
</div></body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = `billet-${booking.reference}.html`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Overlay onClose={onClose}>
      <div style={{ ...modalStyle, maxWidth: 500 }}>
        <ModalHeader title="Votre billet est prêt !" onClose={onClose} />
        <div style={{ padding: "0 24px 24px" }}>
          <div style={{ borderRadius: 14, overflow: "hidden", border: "0.5px solid #e0e0e0" }}>
            <div style={{ background: "linear-gradient(135deg, #14532d, #16a34a)", color: "white", padding: "22px 24px 18px" }}>
              <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: -0.5, marginBottom: 4 }}>Trans<span style={{ color: "#86efac" }}>TiGo</span></div>
              <div style={{ fontSize: 10.5, opacity: 0.7, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 10 }}>Réf : {booking.reference}</div>
              <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: -0.5 }}>
                {booking.trip?.departure} <span style={{ color: "#86efac", margin: "0 8px" }}>→</span> {booking.trip?.arrival}
              </div>
            </div>
            <div style={{ padding: "16px 24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div><div style={tsLabel}>Passager</div><div style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a" }}>{booking.passenger}</div></div>
                {booking.seat && (
                  <div style={{ textAlign: "right" }}>
                    <div style={tsLabel}>Siège</div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: "#16a34a" }}>N°{booking.seat}</div>
                  </div>
                )}
              </div>
              <div style={{ height: 0.5, background: "#f0f0f0", margin: "8px 0 12px" }} />
              {[["Transporteur", booking.trip?.transporter], ["Date", booking.trip?.date], ["Départ", booking.trip?.departureTime], ["Prix", `${Number(booking.trip?.price ?? 0).toLocaleString()} MGA`]].map(([label, val]) => val && (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: "0.5px solid #f8f8f6" }}>
                  <span style={tsLabel}>{label}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a" }}>{val}</span>
                </div>
              ))}
              <div style={cancelNotice}>
                <span style={{ color: "#059669", display: "flex", flexShrink: 0 }}><CancelIcon /></span>
                <div style={{ fontSize: 11.5, color: "#166534" }}>Annulation gratuite jusqu'à 12h avant le départ</div>
              </div>
            </div>
            <div style={{ background: "#f0fdf4", padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: "#16a34a" }}><CheckIcon /> Billet confirmé</div>
              <div style={{ fontSize: 10, letterSpacing: "4px", color: "#ccc", fontFamily: "monospace" }}>||| {booking.reference} |||</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <button style={btnGhost} onClick={() => window.location.reload()}>Fermer</button>
            <button style={{ ...btnPrimary, flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
              onClick={() => { downloadTicket(); window.location.reload(); }}>
              <DownloadIcon /> Télécharger le billet
            </button>
          </div>
        </div>
      </div>
    </Overlay>
  );
};

/* ════════════════════════════════════════════════
   CARPOOL CARD
════════════════════════════════════════════════ */
const CarpoolCard = ({ trip, onBook }) => {
  const accent = accentFor(trip.id);
  const driver = trip.owner?.fullName || trip.owner?.email || "Conducteur";
  const from   = trip.departureCity?.name  || trip.departureCityName  || trip.departure || "—";
  const to     = trip.arrivalCity?.name    || trip.arrivalCityName    || trip.arrival   || "—";

  return (
    <div style={s.card} className="tt-card">
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{ ...s.avatar, background: `${accent}18`, color: accent }}>{initials(driver)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={s.driverName}>{driver}</span>
            <span style={{ ...s.badge, background: `${accent}15`, color: accent }}>{trip.tripType}</span>
          </div>
          {trip.owner?.rating && <Stars rating={trip.owner.rating} />}
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 700, fontFamily: "monospace", letterSpacing: -0.5 }}>{fmtPrice(trip.price)}</div>
          <div style={{ fontSize: 10, color: "#999" }}>MGA / pers.</div>
        </div>
      </div>

      <div style={{ borderLeft: "2px solid #f0f0f0", paddingLeft: 12, marginLeft: 4 }}>
        {[
          { city: from, label: `${fmtDate(trip.date)} ${fmtTime(trip.date)}`, accent: true },
          { city: to,   label: "Arrivée",                                       accent: false },
        ].map((pt, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "5px 0" }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: pt.accent ? accent : "#e0e0e0", flexShrink: 0, marginTop: 3 }} />
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 600 }}>{pt.city}</div>
              <div style={{ fontSize: 11, color: "#bbb" }}>{pt.label}</div>
            </div>
          </div>
        ))}
        {trip.stationName && (
          <div style={{ fontSize: 11, color: "#999", paddingLeft: 20, paddingTop: 4 }}>
            🚌 {trip.stationName}{trip.stationAddress ? ` — ${trip.stationAddress}` : ""}
          </div>
        )}
      </div>

      {trip.photos && trip.photos.length > 0 && (
        <VehiclePhotos photos={trip.photos} />
      )}

      {(() => {
        const deadline = (() => {
          if (!trip.date) return null;
          const dt = new Date(trip.date);
          dt.setHours(dt.getHours() - 2);
          return dt.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
        })();
        return (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10.5, color: "#059669", background: "#ecfdf5", border: "0.5px solid #a7f3d0", borderRadius: 6, padding: "3px 8px" }}>
            <CancelIcon /> Annulation gratuite 12h avant{deadline ? ` · avant le ${deadline}` : ""}
          </div>
        );
      })()}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
          {trip.availableSeats != null && (
            <span style={s.pill}>{trip.availableSeats} place{trip.availableSeats > 1 ? "s" : ""} dispo</span>
          )}
        </div>
        <button style={{ ...s.bookBtn, background: accent, color: "white", border: "none" }}
          onClick={() => onBook(trip)}>Réserver</button>
      </div>
    </div>
  );
};

/* ─── LOADING / EMPTY / ERROR ─── */
const SpinnerBox = () => (
  <div style={{ textAlign: "center", padding: "60px 0", color: "#999", fontSize: 13 }}>
    <div className="tt-spin" style={{ width: 24, height: 24, border: "2px solid #e0e0e0", borderTopColor: "#16a34a", borderRadius: "50%", margin: "0 auto 12px" }} />
    Chargement…
  </div>
);
const Empty  = ({ msg }) => <div style={{ textAlign: "center", padding: "60px 0", fontSize: 14, color: "#999" }}>{msg}</div>;
const ErrBox = ({ msg, onRetry }) => (
  <div style={{ textAlign: "center", padding: "48px 0" }}>
    <div style={{ color: "#ef4444", fontSize: 13, marginBottom: 12 }}>{msg}</div>
    {onRetry && <button style={{ ...s.typeBtn, cursor: "pointer" }} onClick={onRetry}><IcoRefresh /> Réessayer</button>}
  </div>
);

/* ════════════════════════════════════════════════
   HEADER
════════════════════════════════════════════════ */
const Header = () => (
  <header style={s.header}>
    <a href="/" style={s.logo}>Trans<span style={{ color: "#16a34a" }}>TiGo</span></a>
    <nav style={s.nav}>
      <a href="/covoiturage" style={{ ...s.navBtn, ...s.navBtnActive }}>Covoiturage</a>
      <a href="/location" style={s.navBtn}>Location</a>
    </nav>
  </header>
);

/* ════════════════════════════════════════════════
   MAIN PAGE — COVOITURAGE
════════════════════════════════════════════════ */
export default function CovoituragePage() {
  const [trips,    setTrips]    = useState([]);
  const [status,   setStatus]   = useState("loading");
  const [fromCity, setFromCity] = useState(null);
  const [toCity,   setToCity]   = useState(null);
  const [seats,    setSeats]    = useState(1);

  /* Carpool flow state */
  const [flowTrip,       setFlowTrip]       = useState(null);
  const [flowStep,       setFlowStep]       = useState(null);
  const [seatNumber,     setSeatNumber]     = useState(null);
  const [passInfo,       setPassInfo]       = useState(null);
  const [bookingData,    setBookingData]    = useState(null);
  const [ticketData,     setTicketData]     = useState(null);
  const [ticketOperator, setTicketOperator] = useState(null);

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const params = { tripType: "Covoiturage" };
      if (fromCity) params.departure = fromCity.id;
      if (toCity)   params.arrival   = toCity.id;
      if (seats > 1) params.minSeats = seats;
      const res = await api.get("/trips", { params });
      // Filter out trips whose departure time has already passed
      const now = new Date();
      const upcoming = res.data.filter(t => {
        if (!t.date) return true;
        return new Date(t.date) > now;
      });
      setTrips(upcoming);
      setStatus("ok");
    } catch { setStatus("error"); }
  }, [fromCity, toCity, seats]);

  useEffect(() => { load(); }, [load]);

  const handleBook = (trip) => {
    setFlowTrip(trip);
    setSeatNumber(null); setPassInfo(null); setBookingData(null); setTicketData(null);
    setFlowStep("info");
  };

  const handlePassInfo = ({ fullName }) => {
    setPassInfo({ fullName });
    if (Number(flowTrip?.totalSeats) > 0) setFlowStep("seat");
    else createBooking(fullName, null);
  };

  const handleSeatSelected = (seat) => { setSeatNumber(seat); createBooking(passInfo?.fullName, seat); };

  const createBooking = async (fullName, seat) => {
    try {
      const res = await api.post(`/trips/${flowTrip.id}/book`, { fullName, seatNumber: seat ?? undefined });
      setBookingData(res.data);
      setFlowStep("pay");
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || "Une erreur est survenue.";
      Swal.fire({ icon: "error", title: "Erreur de réservation", text: msg, confirmButtonColor: "#d33" });
      setFlowStep(null);
    }
  };

  const handlePaySuccess = ({ operator, txRef }) => {
    setTicketData(bookingData);
    setTicketOperator(operator);
    setFlowStep("ticket");
  };

  const closeFlow = () => { setFlowStep(null); setFlowTrip(null); };

  return (
    <div style={s.page}>
      <style>{css}</style>
      <Header />

      {/* Hero */}
      <div style={s.hero}>
        <div style={s.heroBadge}><span style={s.badgeDot} /> Covoiturages actifs</div>
        <h1 style={s.heroTitle}>
          Partagez <em style={{ color: "#16a34a", fontStyle: "normal" }}>la</em> route
        </h1>
        <p style={s.heroSub}>Voyagez moins cher, rencontrez des gens, réduisez votre empreinte.</p>
      </div>

      {/* Filters */}
      <div style={s.filterRow}>
        <div style={{ ...s.filterItem, minWidth: 180 }}>
          <label style={s.filterLabel}>Départ</label>
          <CityAutocomplete value={fromCity} onChange={setFromCity} placeholder="Toutes les villes" />
        </div>
        <div style={{ ...s.filterItem, minWidth: 180 }}>
          <label style={s.filterLabel}>Arrivée</label>
          <CityAutocomplete value={toCity} onChange={setToCity} placeholder="Toutes les villes" />
        </div>
        <div style={s.filterItem}>
          <label style={s.filterLabel}>Places min.</label>
          <input type="number" min={1} max={20} style={{ ...s.select, width: 72 }} value={seats} onChange={e => setSeats(Number(e.target.value))} />
        </div>
        {status === "ok" && (
          <div style={{ marginLeft: "auto", fontSize: 13, color: "#999", alignSelf: "flex-end", paddingBottom: 2 }}>
            {trips.length} trajet{trips.length !== 1 ? "s" : ""}
          </div>
        )}
      </div>

      {/* Cards */}
      <div style={s.cardList}>
        {status === "loading" && <SpinnerBox />}
        {status === "error"   && <ErrBox msg="Impossible de charger les trajets." onRetry={load} />}
        {status === "ok" && trips.length === 0 && <Empty msg="Aucun covoiturage à venir ne correspond à vos critères." />}
        {status === "ok" && trips.map(t => <CarpoolCard key={t.id} trip={t} onBook={handleBook} />)}
      </div>

      {/* Eco band */}
      <div style={s.ecoBand}>
        {[
          { num: "68%",   label: "moins cher qu'un taxi" },
          { num: "−40%",  label: "d'émissions CO₂" },
          { num: "2 min", label: "pour réserver" },
        ].map(({ num, label }, i, arr) => (
          <React.Fragment key={label}>
            <div style={s.ecoItem}>
              <span style={s.ecoNum}>{num}</span>
              <span style={s.ecoLabel}>{label}</span>
            </div>
            {i < arr.length - 1 && <div style={s.ecoDivider} />}
          </React.Fragment>
        ))}
      </div>

      <footer style={s.footer}>
        <div style={{ fontSize: 13, color: "#999" }}>© 2025 TransTigo · Madagascar</div>
        <div style={{ display: "flex", gap: 16 }}>
          {["Confidentialité", "Conditions", "Contact"].map(l => (
            <a key={l} href={`/${l.toLowerCase()}`} style={{ fontSize: 12, color: "#bbb", textDecoration: "none" }}>{l}</a>
          ))}
        </div>
      </footer>

      {/* Carpool flow modals */}
      {flowStep === "info" && flowTrip && (
        <BookingModal trip={flowTrip} seatNumber={seatNumber} onClose={closeFlow} onConfirm={handlePassInfo} />
      )}
      {flowStep === "seat" && flowTrip && (
        <SeatPicker trip={flowTrip} onSelect={handleSeatSelected} onClose={closeFlow} />
      )}
      {flowStep === "pay" && bookingData && flowTrip && (
        <PaymentModal
          amount={flowTrip.price}
          reference={bookingData.reference}
          onClose={closeFlow}
          onSuccess={handlePaySuccess}
        />
      )}
      {flowStep === "ticket" && ticketData && (
        <TicketModal booking={ticketData} operator={ticketOperator} onClose={closeFlow} />
      )}
    </div>
  );
}

/* ─── SHARED ATOMS ─── */
const overlayStyle = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(8px)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };
const modalStyle   = { background: "white", borderRadius: 20, width: "100%", maxWidth: 460, maxHeight: "92vh", overflowY: "auto", boxShadow: "0 32px 80px rgba(0,0,0,0.25)" };
const modalHeaderStyle = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px 16px", borderBottom: "0.5px solid #f0f0f0", position: "sticky", top: 0, background: "white", zIndex: 1, borderRadius: "20px 20px 0 0" };
const Overlay     = ({ children, onClose }) => <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>{children}</div>;
const ModalHeader = ({ title, onClose }) => (
  <div style={modalHeaderStyle}>
    <span style={{ fontSize: 15, fontWeight: 700, color: "#1a1a1a" }}>{title}</span>
    <button style={{ background: "none", border: "none", cursor: "pointer", color: "#999", display: "flex", padding: 4 }} onClick={onClose}><CloseIcon /></button>
  </div>
);

const tripSummaryCard = { background: "#f0fdf4", borderRadius: 10, padding: "14px 16px", marginBottom: 14, border: "0.5px solid #bbf7d0" };
const tsRow   = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0" };
const tsLabel = { fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.4px", fontWeight: 600 };
const tsVal   = { fontSize: 13, color: "#1a1a1a" };
const cancelNotice = { display: "flex", alignItems: "flex-start", gap: 10, background: "#ecfdf5", border: "0.5px solid #a7f3d0", borderRadius: 10, padding: "12px 14px", marginBottom: 14 };

const amountBox   = { background: "#f0fdf4", borderRadius: 12, padding: "16px 20px", marginBottom: 16, textAlign: "center", border: "0.5px solid #bbf7d0" };
const amountLabel = { fontSize: 11, color: "#16a34a", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 };
const amountVal   = { fontSize: 28, fontWeight: 800, color: "#14532d", letterSpacing: -0.5 };

const payMethodBtn       = { flex: 1, padding: "9px 10px", borderRadius: 9, border: "0.5px solid #e0e0e0", cursor: "pointer", background: "none", fontFamily: "inherit", fontSize: 12, color: "#666", fontWeight: 500, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, transition: "all .15s" };
const payMethodBtnActive = { background: "#1a1a1a", color: "white", borderColor: "#1a1a1a" };
const opBtn              = { flex: 1, padding: "8px 6px", borderRadius: 8, border: "0.5px solid #e0e0e0", cursor: "pointer", background: "none", fontFamily: "inherit", fontSize: 11.5, color: "#666", fontWeight: 500, transition: "all .15s" };
const opBtnActive        = { background: "#14532d", color: "white", borderColor: "#14532d" };
const instructionBox     = { background: "#fffbeb", borderRadius: 10, padding: "12px 14px", border: "0.5px solid #fde68a", marginBottom: 12 };
const stepDot            = { width: 28, height: 28, borderRadius: "50%", border: "2px solid #e0e0e0", background: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#ccc", flexShrink: 0 };
const stepDotActive      = { border: "2px solid #16a34a", background: "#16a34a", color: "white" };
const stepLine           = { width: 60, height: 2, background: "#e0e0e0", margin: "0 4px" };
const fieldLabelStyle    = { fontSize: 11, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 6 };
const inputField2        = { width: "100%", padding: "10px 12px", border: "0.5px solid #e0e0e0", borderRadius: 9, fontSize: 13.5, fontFamily: "inherit", color: "#1a1a1a", background: "#fafaf8", outline: "none", boxSizing: "border-box" };
const btnPrimary         = { padding: "10px 18px", background: "#1a1a1a", color: "white", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6, transition: "background .15s" };
const btnGhost           = { padding: "9px 16px", background: "none", border: "0.5px solid #d0d0d0", borderRadius: 10, cursor: "pointer", fontFamily: "inherit", color: "#444", fontSize: 13, display: "flex", alignItems: "center", gap: 5 };

const s = {
  page:         { fontFamily: "'Plus Jakarta Sans', sans-serif", background: "#fafaf8", color: "#1a1a1a", minHeight: "100vh" },
  header:       { position: "sticky", top: 0, zIndex: 100, background: "rgba(250,250,248,0.95)", backdropFilter: "blur(12px)", borderBottom: "0.5px solid rgba(0,0,0,0.07)", padding: "0 24px", display: "flex", alignItems: "center", gap: 16, height: 60 },
  logo:         { fontSize: 18, fontWeight: 700, letterSpacing: -0.5, color: "#1a1a1a", textDecoration: "none" },
  nav:          { display: "flex", gap: 4, flex: 1, justifyContent: "center" },
  navBtn:       { display: "flex", alignItems: "center", gap: 6, fontSize: 13, padding: "7px 14px", borderRadius: 9, border: "none", background: "none", fontFamily: "inherit", color: "#666", fontWeight: 500, textDecoration: "none" },
  navBtnActive: { background: "#1a1a1a", color: "white" },

  hero:      { padding: "48px 24px 28px", textAlign: "center" },
  heroBadge: { display: "inline-flex", alignItems: "center", gap: 6, background: "#f0fdf4", border: "0.5px solid #bbf7d0", padding: "5px 14px", borderRadius: 20, fontSize: 12, color: "#16a34a", fontWeight: 500, marginBottom: 16 },
  badgeDot:  { width: 6, height: 6, background: "#16a34a", borderRadius: "50%", display: "inline-block" },
  heroTitle: { fontSize: "clamp(28px, 6vw, 44px)", fontWeight: 700, letterSpacing: -1.5, marginBottom: 10, lineHeight: 1.1 },
  heroSub:   { fontSize: 15, color: "#666", lineHeight: 1.7, maxWidth: 520, margin: "0 auto" },

  filterRow:   { maxWidth: 900, margin: "20px auto 0", padding: "0 20px 16px", display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" },
  filterItem:  { display: "flex", flexDirection: "column", gap: 4 },
  filterLabel: { fontSize: 10, color: "#999", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 },
  select:      { fontSize: 13, padding: "8px 10px", border: "0.5px solid #e0e0e0", borderRadius: 8, fontFamily: "inherit", background: "white", color: "#444", outline: "none" },

  cardList:  { maxWidth: 900, margin: "0 auto", padding: "8px 20px 64px", display: "flex", flexDirection: "column", gap: 16 },
  card:      { background: "white", border: "0.5px solid #e8e8e4", borderRadius: 14, padding: "20px", display: "flex", flexDirection: "column", gap: 14 },
  avatar:    { width: 42, height: 42, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 },
  driverName:{ fontSize: 14, fontWeight: 700, letterSpacing: -0.2 },
  badge:     { fontSize: 10, padding: "2px 8px", borderRadius: 20, fontWeight: 600 },
  pill:      { fontSize: 11, padding: "4px 10px", background: "#f4f4f0", borderRadius: 20, color: "#666" },
  bookBtn:   { fontSize: 12, padding: "9px 18px", borderRadius: 9, fontFamily: "inherit", fontWeight: 600, flexShrink: 0, cursor: "pointer" },
  typeBtn:   { fontSize: 12, padding: "7px 14px", borderRadius: 9, border: "0.5px solid #e0e0e0", background: "white", fontFamily: "inherit", color: "#666", display: "flex", alignItems: "center", gap: 6 },

  formField:  { display: "flex", flexDirection: "column", gap: 5, position: "relative" },
  fieldLabel: { fontSize: 10, color: "#999", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, display: "flex", alignItems: "center", gap: 4 },
  fieldInput: { padding: "10px 12px", border: "0.5px solid #e0e0e0", borderRadius: 9, fontSize: 13.5, fontFamily: "inherit", color: "#1a1a1a", background: "#fafaf8", outline: "none", width: "100%", boxSizing: "border-box" },
  dropdown:     { position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "white", border: "0.5px solid #e0e0e0", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.08)", zIndex: 300, listStyle: "none", margin: 0, padding: "4px 0", maxHeight: 220, overflowY: "auto" },
  dropdownItem: { padding: "10px 14px", fontSize: 13, cursor: "pointer", color: "#1a1a1a", background: "transparent" },
  clearBtn:     { position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#bbb", display: "flex", alignItems: "center", padding: 2 },

  ecoBand:   { background: "#f4f4f0", padding: "28px 24px", display: "flex", alignItems: "center", justifyContent: "center", gap: 0, flexWrap: "wrap" },
  ecoItem:   { flex: "1 1 120px", display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "10px 0" },
  ecoNum:    { fontSize: 26, fontWeight: 700, letterSpacing: -1, color: "#1a1a1a" },
  ecoLabel:  { fontSize: 11, color: "#999", textAlign: "center" },
  ecoDivider:{ width: "0.5px", height: 36, background: "#e0e0e0", flexShrink: 0 },
  footer:    { padding: "20px 24px", borderTop: "0.5px solid #e8e8e4", display: "flex", alignItems: "center", justifyContent: "space-between", background: "white", flexWrap: "wrap", gap: 8 },
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
  * { box-sizing: border-box; }
  body { margin: 0; }
  .tt-card { transition: box-shadow .2s, transform .15s; }
  .tt-card:hover { box-shadow: 0 6px 28px rgba(0,0,0,0.08); transform: translateY(-1px); }
  input:focus, select:focus { border-color: #16a34a !important; box-shadow: 0 0 0 3px rgba(22,163,74,0.1) !important; }
  @keyframes tt-spin { to { transform: rotate(360deg); } }
  @keyframes cv-spin { to { transform: rotate(360deg); } }
  .tt-spin { animation: tt-spin .8s linear infinite; }
  @media (max-width: 640px) {
    nav { display: none !important; }
  }
`;